if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        pagination: this.querySelector('[id^="GalleryDots"]')
      };
      this.mql = window.matchMedia('(min-width: 750px)');
      this.desktopThumbsDirectionMql = window.matchMedia('(min-width: 990px)');
      this.swiperLoadAttempts = 0;
      this.viewerSwiper = null;
      this.isThumbnailSliderLayout = this.dataset.desktopLayout === 'thumbnail_slider';

      if (!this.elements.viewer) return;

      if (this.isThumbnailSliderLayout) {
        if (this.elements.thumbnails) {
          this.bindThumbnailArrowScroll();
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }

        this.initializeSwiperGallery();
      } else {
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      if (this.elements.thumbnails && this.dataset.mobileThumbnails === 'show') {
        this.bindMobileThumbnailTouchScroll();
      }

      this.syncThumbnailArrowVisibility();
      this.elements.thumbnails?.addEventListener('scroll', debounce(this.syncThumbnailArrowVisibility.bind(this), 100), { passive: true });
      window.addEventListener('resize', this.syncThumbnailArrowVisibility.bind(this));
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, options = {}) {
      const { scroll = 'if-needed' } = options;

      if (this.viewerSwiper && !prepend) {
        this.refreshSwiperInstances();
        this.slideToMedia(mediaId);
        return;
      }

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        if (this.elements.thumbnails?.slider) this.elements.thumbnails.resetPages();
        if (this.viewerSwiper) {
          this.refreshSwiperInstances();
          this.slideToMedia(mediaId, 0);
        }
      }

      this.preventStickyHeader();

      if (!this.viewerSwiper) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.scrollActiveMediaHorizontally(activeMedia);
            this.scrollActiveMediaIntoView(activeMedia, scroll);
          });
        });
      }

      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      const isDesktopVerticalThumbnailLayout =
        this.dataset.desktopLayout === 'thumbnail_slider' &&
        window.matchMedia('(min-width: 990px)').matches;

      this.elements.thumbnails.slider.scrollTo(
        isDesktopVerticalThumbnailLayout
          ? { top: thumbnail.offsetTop }
          : { left: thumbnail.offsetLeft }
      );
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia && deferredMedia.userEngaged) deferredMedia.loadContent(false);
    }

    resumeActiveMedia() {
      if (!this.viewerSwiper) return;

      const activeMedia = this.viewerSwiper.slides[this.viewerSwiper.activeIndex];
      const deferredMedia = activeMedia?.querySelector('.deferred-media');
      if (!deferredMedia?.userEngaged) return;

      const video = deferredMedia.querySelector('video');
      if (video) video.play().catch(() => {});
    }

    isMediaFullyInView(element) {
      const rect = element.getBoundingClientRect();
      if (!rect.height) return true;

      const padding = 12;
      return rect.top >= padding && rect.bottom <= window.innerHeight - padding;
    }

    shouldScrollPageForMedia(element) {
      return !this.isMediaFullyInView(element);
    }

    shouldSkipPageScrollForActiveMedia() {
      if (this.dataset.desktopLayout === 'stacked') return false;
      if (!this.elements.thumbnails) return false;
      return this.mql.matches;
    }

    scrollActiveMediaIntoView(activeMedia, scroll) {
      if (scroll === 'skip') return;
      if (this.shouldSkipPageScrollForActiveMedia()) return;

      const shouldScroll = scroll === 'always' || this.shouldScrollPageForMedia(activeMedia);
      if (!shouldScroll) return;

      activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }

    scrollActiveMediaHorizontally(activeMedia) {
      const slider = activeMedia.parentElement;
      if (!slider) return;

      const targetLeft = activeMedia.offsetLeft;
      if (Math.abs(slider.scrollLeft - targetLeft) <= 2) return;
      if (slider.scrollWidth <= slider.clientWidth + 2) return;

      slider.scrollTo({ left: targetLeft, behavior: 'smooth' });
    }

    isGallerySlideVisible(slide) {
      if (!slide || slide.hidden || slide.classList.contains('is-variant-color-filtered-out')) return false;
      return window.getComputedStyle(slide).display !== 'none';
    }

    syncSwiperSlidesFromVisibility() {
      if (!this.isThumbnailSliderLayout) return;

      this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((slide) => {
        if (this.isGallerySlideVisible(slide)) {
          slide.classList.add('swiper-slide');
        } else {
          slide.classList.remove('swiper-slide');
        }
      });
    }

    getFirstVisibleSlideIndex() {
      if (!this.viewerSwiper) return -1;

      return Array.from(this.viewerSwiper.slides).findIndex((slide) => this.isGallerySlideVisible(slide));
    }

    slideToFirstVisibleSlide(speed = 0) {
      const visibleIndex = this.getFirstVisibleSlideIndex();
      if (visibleIndex < 0 || !this.viewerSwiper) return;

      if (this.viewerSwiper.activeIndex !== visibleIndex) {
        this.viewerSwiper.slideTo(visibleIndex, speed);
      }
    }

    loadSlideImages(slide) {
      if (!slide) return;

      slide.querySelectorAll('img').forEach((img) => {
        if (img.loading === 'lazy') img.loading = 'eager';
        if (!img.currentSrc && img.dataset.src) img.src = img.dataset.src;
      });
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    isDesktopVerticalThumbnailLayout() {
      return this.dataset.desktopLayout === 'thumbnail_slider' && this.desktopThumbsDirectionMql.matches;
    }

    getVisibleThumbnailItems(thumbList) {
      return Array.from(thumbList.querySelectorAll('[id^="Slide-Thumbnails-"]')).filter((item) => {
        if (item.hidden || item.classList.contains('is-variant-color-filtered-out')) return false;
        return item.offsetWidth > 0 && item.offsetHeight > 0;
      });
    }

    syncThumbnailArrowVisibility() {
      if (!this.elements.thumbnails) return;

      window.requestAnimationFrame(() => {
        const thumbList = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
        const arrowButtons = this.elements.thumbnails.querySelectorAll('button[name="previous"], button[name="next"]');
        if (!thumbList || !arrowButtons.length) return;

        const isDesktopVerticalThumbnailLayout = this.isDesktopVerticalThumbnailLayout();
        const hasScrollableThumbnails = isDesktopVerticalThumbnailLayout
          ? thumbList.scrollHeight > thumbList.clientHeight + 1
          : thumbList.scrollWidth > thumbList.clientWidth + 1;
        arrowButtons.forEach((button) => {
          button.hidden = !hasScrollableThumbnails;
          button.style.display = hasScrollableThumbnails ? 'flex' : 'none';
          button.setAttribute('aria-hidden', hasScrollableThumbnails ? 'false' : 'true');
          if (!hasScrollableThumbnails) {
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
          }
        });
      });
    }

    bindThumbnailArrowScroll() {
      if (!this.elements.thumbnails) return;

      const prevButton = this.elements.thumbnails.querySelector('button[name="previous"]');
      const nextButton = this.elements.thumbnails.querySelector('button[name="next"]');
      if (!prevButton || !nextButton) return;
      const thumbList = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
      if (!thumbList) return;

      this.elements.thumbnails.enableSliderLooping = true;
      const syncArrowState = () => {
        this.syncThumbnailArrowVisibility();
        [prevButton, nextButton].forEach((button) => {
          if (button.hidden) return;
          button.removeAttribute('disabled');
          button.removeAttribute('aria-disabled');
        });
      };
      syncArrowState();

      const handleArrowClick = (event) => {
        if (!this.isThumbnailSliderLayout) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        syncArrowState();

        if (event.currentTarget.hidden) return;

        const thumbItems = this.getVisibleThumbnailItems(thumbList);
        if (!thumbItems.length) return;

        if (this.isDesktopVerticalThumbnailLayout()) {
          const firstVisibleItem = thumbItems[0];
          const nextVisibleItem = thumbItems.find((item) => item !== firstVisibleItem);
          const step = nextVisibleItem
            ? Math.abs(nextVisibleItem.offsetTop - firstVisibleItem.offsetTop)
            : firstVisibleItem.offsetHeight;
          const direction = event.currentTarget.name === 'next' ? 1 : -1;
          thumbList.scrollBy({ top: direction * step, behavior: 'smooth' });
          return;
        }

        const stepCount = Number.parseInt(event.currentTarget.dataset.step, 10) || 1;
        const itemStep = thumbItems[0].offsetWidth;
        const direction = event.currentTarget.name === 'next' ? 1 : -1;
        thumbList.scrollBy({ left: direction * itemStep * stepCount, behavior: 'smooth' });
      };

      prevButton.addEventListener('click', handleArrowClick, true);
      nextButton.addEventListener('click', handleArrowClick, true);
      window.addEventListener('resize', syncArrowState);
    }

    bindMobileThumbnailTouchScroll() {
      const thumbList = this.elements.thumbnails?.querySelector('[id^="Slider-Thumbnails"]');
      if (!thumbList || thumbList.dataset.touchScrollBound === 'true') return;

      thumbList.dataset.touchScrollBound = 'true';

      let startX = 0;
      let startScrollLeft = 0;
      let isDragging = false;

      thumbList.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) return;
        startX = event.touches[0].pageX;
        startScrollLeft = thumbList.scrollLeft;
        isDragging = false;
      }, { passive: true });

      thumbList.addEventListener('touchmove', (event) => {
        if (event.touches.length !== 1) return;
        const deltaX = event.touches[0].pageX - startX;
        if (!isDragging && Math.abs(deltaX) < 8) return;
        isDragging = true;
        thumbList.scrollLeft = startScrollLeft - deltaX;
      }, { passive: true });

      thumbList.addEventListener('touchend', () => {
        if (isDragging) {
          thumbList.dataset.suppressClick = 'true';
          window.setTimeout(() => {
            delete thumbList.dataset.suppressClick;
          }, 300);
        }
        isDragging = false;
      }, { passive: true });

      thumbList.addEventListener('click', (event) => {
        if (thumbList.dataset.suppressClick === 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
    }

    bindDesktopThumbnailArrowScroll() {
      this.bindThumbnailArrowScroll();
    }

    initializeSwiperGallery() {
      if (this.viewerSwiper) return;

      if (typeof window.Swiper !== 'function') {
        if (this.swiperLoadAttempts < 40) {
          this.swiperLoadAttempts += 1;
          window.setTimeout(() => this.initializeSwiperGallery(), 100);
        }
        return;
      }

      const viewerWrapper = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
      if (!viewerWrapper) return;

      this.syncSwiperSlidesFromVisibility();
      this.classList.add('media-gallery--use-swiper');

      const viewerConfig = {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 300,
        watchOverflow: true,
        observer: true,
        observeParents: true
      };

      if (this.dataset.mobileThumbnails === 'hide' && this.elements.pagination) {
        viewerConfig.pagination = {
          el: this.elements.pagination,
          clickable: true
        };
      }

      this.viewerSwiper = new Swiper(this.elements.viewer, viewerConfig);

      this.viewerSwiper.on('transitionStart', () => {
        this.isTransitioning = true;
      });
      this.viewerSwiper.on('transitionEnd', () => {
        this.isTransitioning = false;
        this.resumeActiveMedia();
      });

      this.viewerSwiper.on('slideChange', () => {
        const activeMedia = this.viewerSwiper.slides[this.viewerSwiper.activeIndex];
        if (!this.isGallerySlideVisible(activeMedia)) {
          this.slideToFirstVisibleSlide(0);
          return;
        }
        this.loadSlideImages(activeMedia);
        this.syncActiveSlide(activeMedia);
      });

      this.slideToFirstVisibleSlide(0);
      const activeMedia = this.viewerSwiper.slides[this.viewerSwiper.activeIndex];
      if (activeMedia) {
        this.loadSlideImages(activeMedia);
        this.syncActiveSlide(activeMedia);
      }
    }

    getMediaSlideIndex(mediaId) {
      if (this.viewerSwiper) {
        return Array.from(this.viewerSwiper.slides).findIndex(
          (slide) => slide.dataset.mediaId === mediaId && this.isGallerySlideVisible(slide)
        );
      }

      const mediaSlides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'))
        .filter((slide) => !slide.hidden && !slide.classList.contains('is-variant-color-filtered-out'));
      return mediaSlides.findIndex((slide) => slide.dataset.mediaId === mediaId);
    }

    slideToMedia(mediaId, speed = 300) {
      if (!this.viewerSwiper) return;
      this.refreshSwiperInstances();

      const targetSlideIndex = this.getMediaSlideIndex(mediaId);
      if (targetSlideIndex < 0) return;
      if (this.viewerSwiper.activeIndex === targetSlideIndex) return;

      this.viewerSwiper.slideTo(targetSlideIndex, speed);
    }

    syncActiveSlide(activeMedia) {
      if (!activeMedia || !this.isGallerySlideVisible(activeMedia)) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.toggle('is-active', element === activeMedia);
      });

      this.loadSlideImages(activeMedia);

      const mediaId = activeMedia.dataset.mediaId;
      if (!mediaId || !this.elements.thumbnails) {
        this.playActiveMedia(activeMedia);
        return;
      }

      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      if (activeThumbnail) {
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      this.playActiveMedia(activeMedia);
    }

    refreshSwiperInstances() {
      if (!this.viewerSwiper) return;

      this.syncSwiperSlidesFromVisibility();
      this.viewerSwiper.update();

      const activeMedia = this.viewerSwiper.slides[this.viewerSwiper.activeIndex];
      if (!this.isGallerySlideVisible(activeMedia)) {
        this.slideToFirstVisibleSlide(0);
      }
    }
  });
}
